import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Globe,
  ArrowRight,
  BookOpen,
  Calendar,
  ArrowUpRight,
  X,
  Clock,
  CheckCircle2,
  Sparkles,
  Radio,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import { COVERAGE_METRICS, KNOWLEDGE_HUB_ARTICLES } from "../../Data/dataCoverageData";

import "../../Style/resources/data-coverage.css";

export default function DataCoverage() {
  const [activeArticle, setActiveArticle] = useState(null);

  // Lock background body scroll when reader mode is open
  useEffect(() => {
    if (activeArticle) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeArticle]);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveArticle(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="coverage-page">
      <Seo
        title="Global Data Coverage & Knowledge Hub | Conectify Tech"
        description="Conectify Tech provides intentional business targeting and comprehensive data coverage across 100M+ decision-makers and 19M companies worldwide."
      />

      {/* Top Banner */}
      <section className="coverage-banner">
        <Container>
          <div className="coverage-banner__content">
            <span className="coverage-banner__tag">
              <Globe size={14} /> Global Reach & Telemetry
            </span>
            <h1 className="coverage-banner__title">Data Coverage</h1>
          </div>
        </Container>
      </section>

      {/* Redesigned Premium Intro Section */}
      <section className="coverage-intro">
        <Container>
          <div className="coverage-intro__grid">
            {/* Left Column: Polished Narrative */}
            <motion.div
              className="coverage-intro__content"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55 }}
            >
              <div className="section-eyebrow">
                <Sparkles size={14} className="text-primary" />
                <span>Enterprise Targeting Engine</span>
              </div>

              <h2 className="coverage-intro__heading">
                Data that Builds a{" "}
                <span className="text-gradient">Great Network for Businesses</span>
              </h2>

              <p className="coverage-intro__lead">
                Conectify Tech is here with an intention to serve worldwide with hygiene and intended business targeting—delivering the high-precision coverage modern sales pipelines require.
              </p>

              <p className="coverage-intro__body">
                As a dedicated B2B data provider, we have a clear mission: delivering intelligence with verified accuracy, actionable buyer intent, and zero noise so your team connects directly with real decision-makers.
              </p>

              {/* Value Check Chips */}
              <div className="coverage-intro__chips">
                <div className="coverage-chip">
                  <CheckCircle2 size={16} className="text-primary" />
                  <span>95%+ Field Accuracy SLA</span>
                </div>
                <div className="coverage-chip">
                  <CheckCircle2 size={16} className="text-primary" />
                  <span>Zero Data Degradation Decay</span>
                </div>
                <div className="coverage-chip">
                  <CheckCircle2 size={16} className="text-primary" />
                  <span>Triple-Layer MTV Verification</span>
                </div>
              </div>

              <div className="coverage-intro__actions">
                <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                  Inquire Coverage Scope
                </Button>
              </div>
            </motion.div>

            {/* Right Column: Live Telemetry HUD Card */}
            <motion.div
              className="coverage-intro__hud"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 0.15 }}
            >
              <div className="telemetry-card">
                <div className="telemetry-card__header">
                  <div className="telemetry-status">
                    <Radio size={14} className="telemetry-pulse" />
                    <span>Global Ingestion Active</span>
                  </div>
                  <span className="telemetry-badge">180+ Countries</span>
                </div>

                <div className="telemetry-display">
                  <div className="telemetry-stat">
                    <div className="telemetry-stat__val">100M+</div>
                    <div className="telemetry-stat__lbl">Verified Decision Makers</div>
                  </div>
                  <div className="telemetry-stat">
                    <div className="telemetry-stat__val">19M</div>
                    <div className="telemetry-stat__lbl">Indexed Global Companies</div>
                  </div>
                </div>

                <div className="telemetry-divider" />

                <div className="telemetry-signals">
                  <div className="telemetry-signal-item">
                    <div className="signal-dot signal-dot--green" />
                    <div>
                      <div className="signal-title">C-Suite & VP Lead Feeds</div>
                      <div className="signal-desc">Direct corporate emails, mobile lines & LinkedIn profiles</div>
                    </div>
                  </div>

                  <div className="telemetry-signal-item">
                    <div className="signal-dot signal-dot--blue" />
                    <div>
                      <div className="signal-title">Sales Trigger Monitoring</div>
                      <div className="signal-desc">18 categories of organizational and funding signals</div>
                    </div>
                  </div>

                  <div className="telemetry-signal-item">
                    <div className="signal-dot signal-dot--purple" />
                    <div>
                      <div className="signal-title">Continuous Algorithmic Triangulation</div>
                      <div className="signal-desc">Cross-checked across 40,000+ authoritative sources</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Metrics Grid */}
      <section className="coverage-stats-section">
        <Container>
          <div className="section-title text-center" style={{ marginBottom: "48px" }}>
            <span className="section-eyebrow">Enterprise Telemetry</span>
            <h2 className="section-title__heading">
              Let’s Have a Look on Our <span className="text-gradient">Data Coverage</span>
            </h2>
          </div>

          <div className="coverage-stats-grid">
            {COVERAGE_METRICS.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  className="coverage-stat-card"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                >
                  <div className="coverage-stat-card__icon">
                    <Icon size={22} />
                  </div>
                  <div className="coverage-stat-card__num">{item.value}</div>
                  <div className="coverage-stat-card__label">{item.label}</div>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Knowledge Hub Section */}
      <section className="coverage-hub-section">
        <Container>
          <div className="section-title text-center" style={{ marginBottom: "48px" }}>
            <span className="section-eyebrow">Insights & Intelligence</span>
            <h2 className="section-title__heading">
              What We Have in Our <span className="text-gradient">Knowledge Hub</span>
            </h2>
          </div>

          <div className="coverage-hub-grid">
            {KNOWLEDGE_HUB_ARTICLES.map((article, idx) => (
              <motion.article
                key={article.id}
                className="coverage-article-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: idx * 0.1 }}
              >
                <div className="coverage-article__header">
                  <span className="coverage-article__tag">
                    <BookOpen size={12} /> {article.tag}
                  </span>
                  <span className="coverage-article__date">
                    <Calendar size={13} /> {article.date}
                  </span>
                </div>

                <h3 className="coverage-article__title">{article.title}</h3>
                <p className="coverage-article__excerpt">{article.excerpt}</p>

                <div className="coverage-article__footer">
                  <button
                    type="button"
                    className="coverage-article__link"
                    onClick={() => setActiveArticle(article)}
                  >
                    <span>Read More</span>
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </Container>
      </section>

      {/* Reader Mode Modal Popup */}
      <AnimatePresence>
        {activeArticle && (
          <div className="reader-backdrop" onClick={() => setActiveArticle(null)}>
            <motion.div
              className="reader-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 35, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 25, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Reader Header */}
              <div className="reader-modal__header">
                <div className="reader-modal__meta">
                  <span className="reader-modal__tag">{activeArticle.tag}</span>
                  <span className="reader-modal__date">
                    <Calendar size={13} /> {activeArticle.date}
                  </span>
                  <span className="reader-modal__readtime">
                    <Clock size={13} /> {activeArticle.readTime}
                  </span>
                </div>

                <button
                  type="button"
                  className="reader-modal__close-btn"
                  onClick={() => setActiveArticle(null)}
                  aria-label="Close article"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Reader Body */}
              <div className="reader-modal__scroll">
                <h1 className="reader-modal__title">{activeArticle.title}</h1>

                <div className="reader-modal__content">
                  {activeArticle.content.map((block, i) => {
                    if (block.type === "lead") {
                      return (
                        <p key={i} className="reader-p reader-p--lead">
                          {block.text}
                        </p>
                      );
                    }
                    if (block.type === "subheading") {
                      return (
                        <h4 key={i} className="reader-subheading">
                          {block.text}
                        </h4>
                      );
                    }
                    if (block.type === "point") {
                      return (
                        <div key={i} className="reader-point">
                          <div className="reader-point__badge">{block.number}</div>
                          <div className="reader-point__text">
                            <h5>{block.title}</h5>
                            <p>{block.text}</p>
                          </div>
                        </div>
                      );
                    }
                    if (block.type === "conclusion") {
                      return (
                        <div key={i} className="reader-conclusion">
                          <p>{block.text}</p>
                        </div>
                      );
                    }
                    return (
                      <p key={i} className="reader-p">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                {/* Reader Footer Call-to-Action */}
                <div className="reader-modal__footer">
                  <div className="reader-footer-copy">
                    <h6>Ready to put high-accuracy data to work?</h6>
                    <p>Schedule a 15-minute briefing to audit your target market coverage.</p>
                  </div>
                  <Button icon={<ArrowRight size={16} />} onClick={handleConsultation}>
                    Speak with an Expert
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}